
/**
 * Utility functions for manipulating highlights.
 */
var _require = require("./ranges.js"),
    findFirstAndLastWordIndexes = _require.findFirstAndLastWordIndexes,
    unionRanges = _require.unionRanges,
    spanRanges = _require.spanRanges,
    rangeIncludes = _require.rangeIncludes;

/**
 * Given a list of DOMHighlights, return a new list that also includes the
 * given DOMRange as a new DOMHighlight. If the DOMHighlight intersects
 * existing DOMHighlights, the other Highlights are removed and their ranges
 * are merged into the new DOMHighlight.
 */
var babelPluginFlowReactPropTypes_proptype_DOMRange = require("./types.js").babelPluginFlowReactPropTypes_proptype_DOMRange || require("react").PropTypes.any;

var babelPluginFlowReactPropTypes_proptype_SerializedHighlight = require("./types.js").babelPluginFlowReactPropTypes_proptype_SerializedHighlight || require("react").PropTypes.any;

var babelPluginFlowReactPropTypes_proptype_DOMHighlightSet = require("./types.js").babelPluginFlowReactPropTypes_proptype_DOMHighlightSet || require("react").PropTypes.any;

var babelPluginFlowReactPropTypes_proptype_DOMHighlight = require("./types.js").babelPluginFlowReactPropTypes_proptype_DOMHighlight || require("react").PropTypes.any;

function addHighlight(existingHighlights, newHighlight) {
    var newHighlights = {};

    // Merge the new highlight with any existing highlights that intersect it.
    var mergedDomRange = newHighlight.domRange;
    var mergedFirstWordIndex = newHighlight.firstWordIndex;
    var mergedLastWordIndex = newHighlight.lastWordIndex;
    for (var _iterator = Object.keys(existingHighlights), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
        var _ref;

        if (_isArray) {
            if (_i >= _iterator.length) break;
            _ref = _iterator[_i++];
        } else {
            _i = _iterator.next();
            if (_i.done) break;
            _ref = _i.value;
        }

        var key = _ref;

        var h = existingHighlights[key];
        var newMergedDomRange = unionRanges(h.domRange, mergedDomRange);
        if (newMergedDomRange) {
            // This highlight's range was successfully merged into the new
            // highlight. Update `mergedDomRange`, and *don't* add it to the
            // new set of highlights.
            mergedDomRange = newMergedDomRange;
            mergedFirstWordIndex = Math.min(h.firstWordIndex, mergedFirstWordIndex);
            mergedLastWordIndex = Math.max(h.lastWordIndex, mergedLastWordIndex);
        } else {
            // This highlight's range can't be merged into the new highlight.
            // Add it to the new set of highlights.
            newHighlights[key] = h;
        }
    }

    var newMergedHighlight = {
        firstWordIndex: mergedFirstWordIndex,
        lastWordIndex: mergedLastWordIndex,
        domRange: mergedDomRange
    };

    // Add the newly-merged highlight to the set of highlights, under a new,
    // unique key.
    var existingKeys = Object.keys(newHighlights);
    var newKey = createNewUniqueKey(existingKeys);
    newHighlights[newKey] = newMergedHighlight;

    return newHighlights;
}

/**
 * Given a DOMRange and a list of word ranges, build a corresponding
 * DOMHighlight.
 *
 * If the DOMRange is not a valid highlight given the word ranges, return null.
 */
function buildHighlight(existingHighlights, wordRanges, newHighlightRange) {
    // If any existing highlight fully contains the new highlight range, it's
    // redundant and therefore not valid to build this as a highlight.
    // Return null.
    //
    // NOTE(mdr): Really, our goal is to determine whether the new range's
    //     content is already fully highlighted, so you could imagine this
    //     logic not catching the case where a range's contents are fully
    //     highlighted, but by multiple ranges.
    //
    //     However, words aren't actually adjacent; they have spaces between
    //     them. So, even if each word in the range is currently highlighted,
    //     the space between a pair of words will only be highlighted if
    //     they're included in the same highlight range.
    //
    //     Therefore, if this new range isn't fully contained by an existing
    //     highlight, then there's at *least* an unhighlighted space within the
    //     range. In that case, it makes sense to offer this as a new highlight
    //     that, when added, will merge with the highlights that it intersects.
    for (var _iterator2 = Object.keys(existingHighlights), _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
        var _ref2;

        if (_isArray2) {
            if (_i2 >= _iterator2.length) break;
            _ref2 = _iterator2[_i2++];
        } else {
            _i2 = _iterator2.next();
            if (_i2.done) break;
            _ref2 = _i2.value;
        }

        var key = _ref2;

        var existingHighlightRange = existingHighlights[key].domRange;
        if (rangeIncludes(existingHighlightRange, newHighlightRange)) {
            return null;
        }
    }

    // If the new highlight range doesn't span two words from the content, it's
    // not valid to build this as a highlight. Return null.
    var indexes = findFirstAndLastWordIndexes(newHighlightRange, wordRanges);
    if (!indexes) {
        return null;
    }

    var firstWordIndex = indexes[0],
        lastWordIndex = indexes[1];

    var firstWord = wordRanges[firstWordIndex];
    var lastWord = wordRanges[lastWordIndex];
    return {
        firstWordIndex: firstWordIndex,
        lastWordIndex: lastWordIndex,
        domRange: spanRanges(firstWord, lastWord)
    };
}

/**
 * Given a list of keys, return a new unique key that is not in the list.
 */
function createNewUniqueKey(existingKeys) {
    // The base of the key is the current time, in milliseconds since epoch.
    var base = "" + new Date().getTime();
    if (!existingKeys.includes(base)) {
        return base;
    }

    // But, if the user is a fast-clicker or time-traveler or something, and
    // already has a highlight from this millisecond, then let's attach a
    // suffix and keep incrementing it until we find an unused suffix.
    var suffix = 0;
    var key = void 0;
    do {
        key = base + "-" + suffix;
        suffix++;
    } while (existingKeys.includes(key));

    return key;
}

/**
 * Given a SerializedHightlight and the current set of word ranges, return a
 * DOMHighlight representing the SerializedHighlight.
 *
 * If the SerializedHighlight is not valid given the list of word ranges, throw
 * an error.
 */
function deserializeHighlight(serializedHighlight, wordRanges) {
    var _serializedHighlight$ = serializedHighlight.range,
        firstWordIndex = _serializedHighlight$.firstWordIndex,
        lastWordIndex = _serializedHighlight$.lastWordIndex;


    var firstWord = wordRanges[firstWordIndex];
    if (!firstWord) {
        throw new Error("first word index " + firstWord + " is out of bounds: " + ("must be 0\u2013" + (wordRanges.length - 1) + " inclusive"));
    }

    var lastWord = wordRanges[lastWordIndex];
    if (!lastWord) {
        throw new Error("last word index " + lastWord + " is out of bounds: " + ("must be 0\u2013" + (wordRanges.length - 1) + " inclusive"));
    }

    return {
        firstWordIndex: firstWordIndex,
        lastWordIndex: lastWordIndex,
        domRange: spanRanges(firstWord, lastWord)
    };
}

/**
 * Return a SerializedHighlight representing the given DOMHighlight.
 */
function serializeHighlight(highlight) {
    var firstWordIndex = highlight.firstWordIndex,
        lastWordIndex = highlight.lastWordIndex;


    return {
        range: {
            type: "word-indexes",
            firstWordIndex: firstWordIndex,
            lastWordIndex: lastWordIndex
        }
    };
}

module.exports = {
    addHighlight: addHighlight,
    buildHighlight: buildHighlight,
    deserializeHighlight: deserializeHighlight,
    serializeHighlight: serializeHighlight
};


/*****************
 ** WEBPACK FOOTER
 ** ./src/components/highlighting/highlights.js
 ** module id = 300
 ** module chunks = 0 1
 **/